import { cDeposit } from "../classes/Deposit";
import { Period } from "../classes/Period";
import { iApiConfig, iDepositResponse, iFormattedBN, iPeriodResponse, tAvailableFTokens, tAvailableUTokens, tDiscountPercent, tModalName } from "../interfaces/interfaces";
import { iChrError} from "../interfaces/chrInterfaces";
import { KasUrl, apis, availableFTokens, blockchain, fTokens, isProduction, monitorUrl, projectName, uTokens } from "../settings";
import { eAppActionTypes } from "../store/app/appTypes";
import { store } from "../store/store"
import { checkValidAddress } from "./checkValidAddress";
import { cBigNumber } from "../classes/BigNumber";

type tDateFormat =
	'YYYY-MM-DD' |
	'YYYY-MM-DD hh:mm' |
	'YYYY-MM-DD hh:mm:ss' |
	'DDMMMYY' |
	'DDmmmYY' |
	'mmm DD' |
	'YYYY.MM.DD' |
	'DD mmm YY, hh:mm' |
	'hh:mm' |
	'hh:mm:ss' |
	'YYYY-MM-DDThh:mm' |
	'YYYY-MM-DDThh:mm:ss'
;
export const getHumanDate = (ts: number, format: tDateFormat): string => {
	if(Number.isNaN(ts) || !Number.isFinite(ts)) throw new Error('Error code: 6_9');

	const date = new Date(ts * 1000);

	const year = date.getUTCFullYear().toString();
	const month = (date.getUTCMonth()+1).toString().padStart(2, '0');
	const day = date.getUTCDate().toString().padStart(2, '0');
	const hour = date.getUTCHours().toString().padStart(2, '0');
	const minutes = date.getUTCMinutes().toString().padStart(2, '0');
	const seconds = date.getUTCSeconds().toString().padStart(2, '0');

	const monthNumber = date.getUTCMonth();
	const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
	const monthsCapital = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];

	switch (format) {
		case 'YYYY-MM-DD':
			return `${year}-${month}-${day}`;

		case 'YYYY-MM-DD hh:mm':
			return `${year}-${month}-${day} ${hour}:${minutes}`;

		case 'YYYY-MM-DD hh:mm:ss':
			return `${year}-${month}-${day} ${hour}:${minutes}:${seconds}`;

		case 'DDMMMYY':
			return `${day}${monthsCapital[monthNumber]}${year.slice(-2)}`;

		case 'DDmmmYY':
			return `${day}${months[monthNumber]}${year.slice(-2)}`;

		case 'mmm DD':
			return `${months[monthNumber]} ${day}`;

		case 'YYYY.MM.DD':
			return `${year}.${month}.${day}`;

		case 'DD mmm YY, hh:mm':
			return `${day} ${months[monthNumber]} ${year.slice(-2)}, ${hour}:${minutes}`;

		case 'hh:mm':
			return `${hour}:${minutes}`;

		case 'hh:mm:ss':
			return `${hour}:${minutes}:${seconds}`;

		case 'YYYY-MM-DDThh:mm':
			return `${year}-${month}-${day}T${hour}:${minutes}`;

		case 'YYYY-MM-DDThh:mm:ss':
			return `${year}-${month}-${day}T${hour}:${minutes}:${seconds}`;

		default:
			return `${year}-${month}-${day} ${hour}:${minutes}:${seconds}`;
	}
}

/**
 * Works as default toFixed but not rounds.
 * 
 * a lot of bugs
*/
export const toFixed = (value: number | string, length: number, removeZeroes: boolean = true): string => {
	if (typeof(value) === 'number'){
		value = value.toString();
	}
	let splitted = value.split('.');
	if (splitted[1]){
		for (let i=1; i<length; i++){
			splitted[1] = splitted[1] + '0';
		}
	}
	const res = splitted.length === 2
		? splitted[0] + '.' + splitted[1].substring(0,length)
		: splitted[0]
	;
	return removeZeroes? res.replace(/0*$/,"") : res;
}

/*
export const getApiConfig = (): iApiConfig => {
	const { apiData, discountPercent } = store.getState().app;

	return apiData[discountPercent]?.config ;
} */

// add second fetch -?
export const getConfig = async (percent?: tDiscountPercent): Promise<iApiConfig> => {
	const { discountPercent, apiData } = store.getState().app;
	const finalPercent = percent || discountPercent;

	if (apiData[discountPercent]?.config) return apiData[discountPercent]!.config;

	const finalApi = apiData[finalPercent]?.api || apis[finalPercent].main

	try {
		// const response = await fetch(finalApi + 'info2/')	// 404 test
		const response = await fetch(finalApi + 'info/')
		// console.log('res: ', response);
		if (!response.ok) throw new Error('main response not ok');	// catches here

		const res = await response.json();
		if (!isConfig(res)) throw new Error('wrong response');

		if (!apiData[finalPercent]){
			store.dispatch({
				type: eAppActionTypes.SET_API_DATA,
				payload: {
					percent: finalPercent,
					api: finalApi,
					config: res
				}
			});
		}
		return res;

	} catch (err) {
		console.log('fetchConfig catch: ', err);
		if (!apis[finalPercent].reserve) throw new Error('no available api');

		const response = await fetch(
			(apis[finalPercent].reserve) + 'info/'
		)
		if (!response.ok) throw new Error('reserve back not available');

		const res = await response.json();
		if (!isConfig(res)) throw new Error('wrong response');

		if (!apiData[finalPercent]){
			store.dispatch({
				type: eAppActionTypes.SET_API_DATA,
				payload: {
					percent: finalPercent,
					api: apis[finalPercent].reserve!,
					config: res
				}
			});
		}

		return res;
	}
}

const isConfig = (config: any): config is iApiConfig => {
	const resConfig = config as iApiConfig;

    return (
		/* 'net' in resConfig && typeof resConfig.net === 'object' &&
			'chainID' in resConfig.net && typeof resConfig.net.chainID === 'string' &&
			'httpProvider' in resConfig.net && typeof resConfig.net.httpProvider === 'string' && */
		'sbd' in resConfig && typeof resConfig.sbd === 'object' &&
			'abi' in resConfig.sbd && typeof resConfig.sbd.abi === 'string' &&
			'adr' in resConfig.sbd && typeof resConfig.sbd.adr === 'string' &&
			checkValidAddress(resConfig.sbd.adr) &&
			'end_time' in resConfig.sbd && typeof resConfig.sbd.end_time === 'number' &&
			'in_work' in resConfig.sbd && typeof resConfig.sbd.in_work === 'number' &&
			// 'strike_mult' in resConfig.sbd && typeof resConfig.sbd.strike_mult === 'string' &&
		'tokens' in resConfig && typeof resConfig.tokens === 'object' &&
			'f_tok' in resConfig.tokens && typeof resConfig.tokens.f_tok === 'object' &&
				'adr' in resConfig.tokens.f_tok && typeof resConfig.tokens.f_tok.adr === 'string' &&
				checkValidAddress(resConfig.tokens.f_tok.adr) &&
				'dec' in resConfig.tokens.f_tok && typeof resConfig.tokens.f_tok.dec === 'number' &&
				'name' in resConfig.tokens.f_tok && typeof resConfig.tokens.f_tok.name === 'string' &&
			'tok_abi' in resConfig.tokens && typeof resConfig.tokens.tok_abi === 'string' &&
			'u_tok' in resConfig.tokens && typeof resConfig.tokens.u_tok === 'object' &&
				'adr' in resConfig.tokens.u_tok && typeof resConfig.tokens.u_tok.adr === 'string' &&
				checkValidAddress(resConfig.tokens.u_tok.adr) &&
				'dec' in resConfig.tokens.u_tok && typeof resConfig.tokens.u_tok.dec === 'number' &&
				'name' in resConfig.tokens.u_tok && typeof resConfig.tokens.u_tok.name === 'string'
	);
}

// export const updateDeposits = async (percent: tDiscountPercent): Promise<Deposit[]> => {
export const updateDeposits = async (percent: tDiscountPercent) => {
	// const { discountPercent, apiData, userAddress } = store.getState().app;
	const { apiData, userAddress } = store.getState().app;
	// const finalPercent = percent || discountPercent;
	const finalPercent = percent;
	if (!userAddress || !apiData[finalPercent]) throw new Error("");

	const finalApi = apiData[finalPercent]?.api || apis[finalPercent].main;

	try {
		const response = await fetch(finalApi + 'deposits/deposits?client_address=' + userAddress);
		// console.log('res: ', response);
		if (!response.ok) throw new Error('main response not ok');	// catches here

		const res: iDepositResponse[] = await response.json();
		if (!Array.isArray(res) || res.map(elem => isDeposit(elem)).includes(false)) throw new Error('wrong response');

		store.dispatch({
			type: eAppActionTypes.SET_DEPOSITS,
			payload: {
				percent: finalPercent,
				deposits: res
					.map(depositResponse => depositResponse.finished === true? undefined : new cDeposit(depositResponse, apiData[finalPercent]!.config.sbd.strike_mult))
					.filter(item => item !== undefined) as cDeposit[]
			}
		});
		/* return res
			.map(depositResponse => depositResponse.finished === true? undefined : new Deposit(depositResponse, apiData[finalPercent]!.config.sbd.strike_mult))
			.filter(item => item !== undefined) as Deposit[]
		; */

	} catch (err) {
		console.log('fetchDeposits catch: ', err);
		if (!apis[finalPercent].reserve) throw new Error('no available api');

		const response = await fetch((apis[finalPercent].reserve) + 'deposits/deposits?client_address=' + userAddress)
		if (!response.ok) throw new Error('reserve back not available');

		const res: iDepositResponse[] = await response.json();	// iDeposit[]
		if (!Array.isArray(res) || res.map(elem => isDeposit(elem)).includes(false)) throw new Error('wrong response');

		store.dispatch({
			type: eAppActionTypes.SET_DEPOSITS,
			payload: {
				percent: finalPercent,
				deposits: res
					.map(depositResponse => depositResponse.finished === true? undefined : new cDeposit(depositResponse, apiData[finalPercent]!.config.sbd.strike_mult))
					.filter(item => item !== undefined) as cDeposit[]
			}
		});
		/* return res
			.map(depositResponse => depositResponse.finished === true? undefined : new Deposit(depositResponse, apiData[finalPercent]!.config.sbd.strike_mult))
			.filter(item => item !== undefined) as Deposit[]
		; */
	}
}

const isDeposit = (response: any): response is iDepositResponse => {
	const deposit = response as iDepositResponse;

    return (
		'c' in deposit && typeof deposit.c === 'string' &&
		'can_withdraw' in deposit && typeof deposit.can_withdraw === 'string' &&
		'client' in deposit && typeof deposit.client === 'string' &&
		checkValidAddress(deposit.client) &&
		'close_c' in deposit && typeof deposit.close_c === 'string' &&
		'depo_amt' in deposit && typeof deposit.depo_amt === 'string' &&
		'end_time' in deposit && typeof deposit.end_time === 'string' &&
		'finished' in deposit && typeof deposit.finished === 'boolean' &&
		'ft_amt' in deposit && typeof deposit.ft_amt === 'string' &&
		'id' in deposit && typeof deposit.id === 'number' &&
		'l_ft_amt' in deposit && typeof deposit.l_ft_amt === 'string' &&
		'l_ut_amt' in deposit && typeof deposit.l_ut_amt === 'string' &&
		'l_ut_price' in deposit && typeof deposit.l_ut_price === 'string' &&
		'period' in deposit && typeof deposit.period === 'number' &&
		'ut_amt' in deposit && typeof deposit.ut_amt === 'string'
	);
}

export const getLastPeriod = async (percent: tDiscountPercent): Promise<Period | null> => {
	const { apiData } = store.getState().app;
	const finalPercent = percent;
	if (!apiData[finalPercent]) throw new Error("");

	const finalApi = apiData[finalPercent]?.api || apis[finalPercent].main;

	try {
		const response = await fetch(finalApi + 'periods/last_period');
		if (!response.ok) throw new Error('main response not ok');	// catches here

		const res: [iPeriodResponse] | null = await response.json();
		if (
			res !== null &&
			!(Array.isArray(res) && isPeriod(res[0]))
		) throw new Error('wrong response');

		/* if (!apiData[finalPercent]){
			store.dispatch({
				type: eAppActionTypes.SET_API_DATA,
				payload: {
					percent: finalPercent,
					api: finalApi,
					config: res
				}
			});
		} */

		return res? new Period(res[0]) : res;

	} catch (err) {
		if (!apis[finalPercent].reserve) throw new Error('no available api');

		const response = await fetch((apis[finalPercent].reserve) + 'periods/last_period');
		if (!response.ok) throw new Error('reserve back not available');

		const res: [iPeriodResponse] = await response.json();
		// if (!Array.isArray(res) || isPeriod(res[0])) throw new Error('wrong response');
		if (
			res !== null &&
			!(Array.isArray(res) && isPeriod(res[0]))
		) throw new Error('wrong response');

		/* if (!apiData[finalPercent]){
			store.dispatch({
				type: eAppActionTypes.SET_API_DATA,
				payload: {
					percent: finalPercent,
					api: apis[finalPercent].reserve!,
					config: res
				}
			});
		} */
		return res? new Period(res[0]) : res;
	}
}

const isPeriod = (response: any): response is iPeriodResponse => {
	const period = response as iPeriodResponse;

    return (
		'num' in period && typeof period.num === 'number' &&
		'launch_num' in period && typeof period.launch_num === 'number' &&
		'ft_b' in period && typeof period.ft_b === 'string' &&
		'ft_e' in period && typeof period.ft_e === 'string' &&
		'ut_b' in period && typeof period.ut_b === 'string' &&
		'ut_e' in period && typeof period.ut_e === 'string' &&
		'c_b' in period && typeof period.c_b === 'string' &&
		'c_e' in period && typeof period.c_e === 'string' &&
		'end_time' in period && typeof period.end_time === 'number' &&
		'opt_id' in period && typeof period.opt_id === 'number' &&
		'created_amt' in period && typeof period.created_amt === 'string' &&
		'ord_id' in period && typeof period.ord_id === 'number' &&
		'finished' in period && typeof period.finished === 'boolean'
	);
}

/* const getPaths = ['1', '2'] as const;
type tPaths = typeof getPaths[number];

type args = {
	path: tPaths
}

const a: args =

// GET - temp
export const GET = (path: tPaths):  => {

} */

export const fixExponential = (value: string | number): string => {
	const data = value.toString().split(/[eE]/);
	if (data.length === 1) return data[0];

	let z = '',
		sign = +value < 0 ? '-' : '',
		str = data[0].replace('.', ''),
		mag = Number(data[1]) + 1
	;

	if (mag < 0) {
		z = sign + '0.';
		while (mag++) z += '0';

		return z + str.replace(/^-/, '');
	}

	mag -= str.length;
	while (mag--) z += '0';

	return str + z;
}

export const connectKAS = async () => {
	const username = 'hello';
	const password = 'world';

	const res = await fetch(KasUrl + 'users/auth', {
			method: 'POST',
			headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
			},
			body: `username=${username}&password=${password}`
	})

	if (res.ok) {
		const accessToken = (await res.json()).access_token;
		store.dispatch({
			type: eAppActionTypes.SET_KAS_ACCESS_TOKEN,
			payload: accessToken
		});
	}
}

export const logErrorKas = async (error: string) => {
	switch (blockchain) {
		case 'chromia':
			const err = error as unknown as iChrError;
			if (err && err.originalError && err.originalError.info && err.originalError.info.error && err.originalError.info.error.code === 4001) return;
	}

	const accessToken =  store.getState().app.kasAccessToken;

	if (isProduction && accessToken) {
		return fetch(`${KasUrl}users/send_message?project_name=${projectName}&text=${error}`, {
			method: 'GET',
			headers: {
					'Authorization': `bearer ${accessToken}`
			},
		})
	}

	throw new Error(error);
}

const isFundingToken = (token: tAvailableFTokens | tAvailableUTokens): token is tAvailableFTokens => {
	return availableFTokens.includes(token as any);
}

// export const getFormattedBN = (value: string, type: 'funding' | 'underlying', discountPercent: tDiscountPercent, visibleLength: number = 9): iFormattedBN => {
export const getFormattedBN = (value: string | cBigNumber, token: tAvailableFTokens | tAvailableUTokens, visibleLength: number = 9): iFormattedBN => {
	const valueBN = value instanceof cBigNumber? value : new cBigNumber(value);
	// const minValue = new cBigNumber(1).div(10**(type === 'funding'? fTokens : uTokens)[discountPercent].showDecimals, {raiseValue: true});
	// const minValue = new cBigNumber(1, {currentDecimals: visibleDecimals});
	// const minValue = new cBigNumber(1, {currentDecimals: (type === 'funding'? fTokens : uTokens)[discountPercent].showDecimals});

	const minValue = new cBigNumber(1, {currentDecimals: (isFundingToken(token)? fTokens[token] : uTokens[token]).showDecimals});
	/* if (valueBN.gte(1000000, {raiseValue: true})){
		setIsLonger(true);
		setResValue( Math.trunc(+valueBN.div(1000000, {raiseValue: true}).valueStr) + 'M' );
		return;
	} */

	if (valueBN.lt(minValue) && valueBN.gtZero()){
		// setIsLonger(true);
		// setResValue('<'+minValue.valueStr);
		return {
			value: valueBN,
			formatted: '<' + minValue.valueStr
		};
	}

	const splitted = valueBN.valueStr.split('.');
	if (splitted[1] && splitted[1].length < visibleLength){
		// let res = splitted[0] + '.' + +toFixed(splitted[1], visibleDecimals);	// removes zeroes at start
		let res = splitted[0] + '.' + splitted[1].substring(0, visibleLength).replace(/0*$/,"");

		if (res.length > visibleLength) {
			res = +res.slice(0, visibleLength) + '';
		}
		// setIsLonger(true);
		// setResValue(res);
		// remove zeros -?
		return {
			value: valueBN,
			formatted: res
		};
	}

	// setIsLonger(valueBN.valueStr.length > visibleLength);
	// setResValue( +valueBN.valueStr.slice(0, visibleLength) + '' );
	return {
		value: valueBN,
		formatted:  +valueBN.valueStr.slice(0, visibleLength) + ''
	}

	/* return {
		value: valueBN,
		formatted: valueBN.valueBN18.gt(zeroBN)
			? valueBN.valueBN18.lt(minValue.valueBN18)
				? '<' + minValue.valueStr
				// : toFixed(amountBN.valueStr, (type === 'funding'? fTokens : uTokens)[discountPercent].showDecimals)
				: toFixed(valueBN.valueStr)
			: '0'
	}; */
}

export const dispatchCloseModalEvent = (modalName: tModalName, modalId: number) => {
	document.dispatchEvent(new Event(`close_${modalName}_${modalId}`));
}

export const logUser = async (address: string) => {
	fetch(monitorUrl + '/accounts', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			client: address,
			project: "pickaxe",
		})
	})
}